import type { UpdateBugReportRequest } from '@/api/bug-reports'
import { bugReportsApi } from '@/api/bug-reports'
import { BugReportService, type BugReport } from '@/services/bugReportService'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useBugReportStore = defineStore('bugReport', () => {
  // Cache de tous les bugs (chargés une seule fois)
  const allBugReports = ref<BugReport[]>([])
  const currentBugReport = ref<BugReport | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastFetchTime = ref<number | null>(null)

  // Cache des screenshots (Map<id, blobUrl>)
  const screenshotCache = ref<Map<number, string>>(new Map())
  // Cache des IDs sans screenshot (Set<id>) pour éviter les requêtes répétées
  const noScreenshotCache = ref<Set<number>>(new Set())

  const stats = computed(() => {
    if (allBugReports.value.length === 0) return null
    return {
      open: allBugReports.value.filter(br => br.status === 'open').length,
      in_progress: allBugReports.value.filter(br => br.status === 'in_progress').length,
      resolved: allBugReports.value.filter(br => br.status === 'resolved').length,
      closed: allBugReports.value.filter(br => br.status === 'closed').length,
    }
  })

  /**
   * Charger TOUTES les remontées une seule fois (avec pagination automatique)
   * Ne recharge que si forceRefresh=true ou si jamais chargé
   */
  async function loadAllBugReports(forceRefresh = false): Promise<void> {
    // Ne pas recharger si déjà chargé récemment et pas de force refresh
    if (!forceRefresh && allBugReports.value.length > 0 && lastFetchTime.value) {
      const timeSinceLastFetch = Date.now() - lastFetchTime.value
      // Cache valide pendant 5 minutes
      if (timeSinceLastFetch < 5 * 60 * 1000) {
        console.log('[BdC-BugReportStore] Utilisation du cache, pas de rechargement')
        return
      }
    }

    isLoading.value = true
    error.value = null
    try {
      console.log('[BdC-BugReportStore] Chargement de TOUTES les remontées')

      // Charger tous les bugs en faisant plusieurs requêtes paginées (limit max = 100)
      const allReports: BugReport[] = []
      const limit = 100 // Limite maximale du backend
      let offset = 0
      let hasMore = true

      while (hasMore) {
        const response = await BugReportService.getBugReports({ limit, offset })
        allReports.push(...response.data)

        // Vérifier s'il y a encore des résultats
        hasMore = response.meta.hasNext
        offset += limit

        console.log(
          `[BdC-BugReportStore] Chargé ${allReports.length}/${response.meta.total} remontées`
        )
      }

      allBugReports.value = allReports
      lastFetchTime.value = Date.now()
      console.log(`[BdC-BugReportStore] ${allReports.length} remontées chargées en cache`)
    } catch (err) {
      console.error('[BdC-BugReportStore] Erreur chargement remontées:', err)
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des remontées'
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Filtrer et trier les remontées côté frontend
   */
  function getFilteredAndSortedBugReports(filters: {
    status?: string | string[]
    priority?: string
    category?: string
    search?: string
    userId?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    limit?: number
    offset?: number
  }) {
    let filtered = [...allBugReports.value]

    // Filtres
    if (filters.userId) {
      filtered = filtered.filter(br => br.userId === filters.userId)
    }
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        filtered = filtered.filter(br => filters.status!.includes(br.status))
      } else {
        filtered = filtered.filter(br => br.status === filters.status)
      }
    }
    if (filters.priority) {
      filtered = filtered.filter(br => br.priority === filters.priority)
    }
    if (filters.category) {
      filtered = filtered.filter(br => br.category === filters.category)
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(
        br =>
          br.title.toLowerCase().includes(searchLower) ||
          br.description.toLowerCase().includes(searchLower)
      )
    }

    // Tri
    const sortBy = filters.sortBy || 'createdAt'
    const sortOrder = filters.sortOrder || 'desc'

    filtered.sort((a, b) => {
      let aValue: any = a[sortBy as keyof BugReport]
      let bValue: any = b[sortBy as keyof BugReport]

      // Gestion des dates
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }

      // Gestion des priorités (ordre: critical > high > medium > low)
      if (sortBy === 'priority') {
        const priorityOrder: Record<string, number> = {
          critical: 4,
          high: 3,
          medium: 2,
          low: 1,
        }
        aValue = priorityOrder[aValue] || 0
        bValue = priorityOrder[bValue] || 0
      }

      // Gestion des statuts (ordre: open > in_progress > resolved > closed)
      if (sortBy === 'status') {
        const statusOrder: Record<string, number> = {
          open: 4,
          in_progress: 3,
          resolved: 2,
          closed: 1,
        }
        aValue = statusOrder[aValue] || 0
        bValue = statusOrder[bValue] || 0
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    // Pagination côté frontend
    const offset = filters.offset || 0
    const limit = filters.limit || 20
    const paginated = filtered.slice(offset, offset + limit)

    return {
      data: paginated,
      meta: {
        total: filtered.length,
        limit,
        offset,
        hasNext: offset + limit < filtered.length,
        hasPrevious: offset > 0,
      },
    }
  }

  /**
   * Charger un screenshot et le mettre en cache
   */
  async function getScreenshot(id: number): Promise<string | null> {
    // Vérifier le cache des screenshots existants
    if (screenshotCache.value.has(id)) {
      return screenshotCache.value.get(id) || null
    }

    // Vérifier le cache des screenshots inexistants (évite les requêtes 404 répétées)
    if (noScreenshotCache.value.has(id)) {
      return null
    }

    try {
      console.log(`[BdC-BugReportStore] Chargement screenshot ${id}`)
      const url = await bugReportsApi.getScreenshot(id)

      if (url) {
        screenshotCache.value.set(id, url)
        return url
      } else {
        // Screenshot n'existe pas, mettre en cache pour éviter les requêtes futures
        noScreenshotCache.value.add(id)
        return null
      }
    } catch (err) {
      console.error(`[BdC-BugReportStore] Erreur chargement screenshot ${id}:`, err)
      return null
    }
  }

  /**
   * Nettoyer le cache des screenshots
   */
  function clearScreenshotCache() {
    screenshotCache.value.forEach(url => {
      URL.revokeObjectURL(url)
    })
    screenshotCache.value.clear()
    noScreenshotCache.value.clear()
  }

  /**
   * Charger une remontée par ID
   */
  async function loadBugReportById(id: number): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      console.log('[BdC-BugReportStore] Chargement remontée:', id)
      const bugReport = await BugReportService.getBugReportById(id)
      currentBugReport.value = bugReport
      console.log('[BdC-BugReportStore] Remontée chargée:', bugReport.id)
    } catch (err) {
      console.error('[BdC-BugReportStore] Erreur chargement remontée:', err)
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement de la remontée'
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Charger les statistiques (maintenant calculées côté frontend)
   */
  async function loadStats(): Promise<void> {
    // Les stats sont maintenant calculées depuis allBugReports
    // On charge juste les bugs si pas encore chargés
    if (allBugReports.value.length === 0) {
      await loadAllBugReports()
    }
  }

  /**
   * Mettre à jour une remontée
   */
  async function updateBugReport(id: number, data: UpdateBugReportRequest): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      console.log('[BdC-BugReportStore] Mise à jour remontée:', id, data)
      const updated = await BugReportService.updateBugReport(id, data)

      // Mettre à jour dans le cache
      const index = allBugReports.value.findIndex((br: BugReport) => br.id === id)
      if (index !== -1) {
        allBugReports.value[index] = updated
      }

      // Mettre à jour la remontée courante si c'est la même
      if (currentBugReport.value?.id === id) {
        currentBugReport.value = updated
      }

      console.log('[BdC-BugReportStore] Remontée mise à jour:', updated.id)
    } catch (err) {
      console.error('[BdC-BugReportStore] Erreur mise à jour remontée:', err)
      error.value =
        err instanceof Error ? err.message : 'Erreur lors de la mise à jour de la remontée'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Supprimer une remontée
   */
  async function deleteBugReport(id: number): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      console.log('[BdC-BugReportStore] Suppression remontée:', id)
      await BugReportService.deleteBugReport(id)

      // Retirer du cache
      allBugReports.value = allBugReports.value.filter((br: BugReport) => br.id !== id)

      // Nettoyer le cache du screenshot
      if (screenshotCache.value.has(id)) {
        const url = screenshotCache.value.get(id)
        if (url) URL.revokeObjectURL(url)
        screenshotCache.value.delete(id)
      }
      // Nettoyer aussi le cache "pas de screenshot"
      noScreenshotCache.value.delete(id)

      // Vider la remontée courante si c'est la même
      if (currentBugReport.value?.id === id) {
        currentBugReport.value = null
      }

      console.log('[BdC-BugReportStore] Remontée supprimée:', id)
    } catch (err) {
      console.error('[BdC-BugReportStore] Erreur suppression remontée:', err)
      error.value =
        err instanceof Error ? err.message : 'Erreur lors de la suppression de la remontée'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Réinitialiser le store
   */
  function reset(): void {
    allBugReports.value = []
    currentBugReport.value = null
    lastFetchTime.value = null
    error.value = null
    clearScreenshotCache()
  }

  return {
    allBugReports,
    currentBugReport,
    isLoading,
    error,
    stats,
    loadAllBugReports,
    getFilteredAndSortedBugReports,
    getScreenshot,
    loadBugReportById,
    loadStats,
    updateBugReport,
    deleteBugReport,
    reset,
  }
})
